import { IMG, PEOPLE, TIMELINE, FACTS, sized } from "@/lib/content";
import { Reveal } from "@/components/public/Reveal";
import { Btn, SectionHead, Facts, Photo, CTA, DimLine, Eyebrow } from "@/components/public/ui";

const telHref = (s) => `tel:+47${s.replace(/\s/g, "")}`;

export default function About() {
  return (
    <div data-testid="public-about-page">
      {/* Intro */}
      <section className="container-public pt-10 sm:pt-14 lg:pt-20" data-testid="public-page-head">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <Eyebrow className="mb-6">Om oss</Eyebrow>
            <h1 className="t-display max-w-[14ch] text-ink">Eit fagmiljø i Kvinnherad.</h1>
          </div>
          <div className="flex flex-col justify-end lg:col-span-5 lg:pb-2">
            <p className="text-[1.0625rem] leading-[1.55] text-ink sm:text-[1.125rem]">Kvinnherad Elektro AS har levert elektrotenester i over 80 år. I dag er vi om lag 50 tilsette, med hovudkontor i Rosendal og avdelingar på Husnes og Halsnøy. Vi er godkjend lærebedrift.</p>
            <dl className="mt-8 grid grid-cols-2 gap-4">
              {FACTS.slice(0, 2).map(([v, l]) => (
                <div key={l} className="border-t border-border pt-3">
                  <dd className="t-num text-3xl text-ink">{v}</dd>
                  <dt className="mt-1 text-[13px] leading-snug text-muted-foreground">{l}</dt>
                </div>
              ))}
            </dl>
          </div>
        </div>
        <Photo className="mt-12 sm:mt-16" src={sized(IMG.fjord_village, 2400)} alt="Bygd ved Hardangerfjorden med snødekte fjell" ratio="aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9]" sizes="100vw" priority />
      </section>

      <section className="container-public pt-24 sm:pt-32" data-testid="public-about-statement">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-7">
            <h2 className="t-h2 text-ink">Same krav på smelteverket som i stova.</h2>
            <div className="mt-6 space-y-5 text-[17px] leading-relaxed text-muted-foreground">
              <p>Vi leverer til private kundar og lokale verksemder, og er samtidig med på store prosjekt i distriktet: tunnelar for Statens vegvesen, utvidinga ved Boliden Odda og arbeid på Hydro Husnes. Service-elektrikarane våre har lang erfaring og velutstyrte servicebilar.</p>
              <p>Anten det gjeld nybygg, påbygg eller oppgradering av eit eksisterande anlegg, hjelper vi med alt frå planlegging til gjennomføring og godkjenning. Vi tilbyr òg el-sjekk, internkontroll for bedrifter og årleg gjennomgang av det elektriske anlegget.</p>
            </div>
          </Reveal>
          <Reveal delay={0.06} className="lg:col-span-5">
            <Photo src={sized(IMG.electrician_woman, 1200)} alt="Elektrikar ved fordelingsskap" ratio="aspect-[4/5]" />
          </Reveal>
        </div>
        <DimLine className="mt-16" />
        <Facts items={FACTS} className="pt-6" data-testid="public-about-facts" />
      </section>

      <section className="container-public pt-24 sm:pt-32" data-testid="public-about-timeline">
        <SectionHead eyebrow="Historie" title="Frå bygdeelektrikar til industripartnar" text="Milepælar slik verksemda sjølv har omtalt dei på kvinnheradelektro.no, og EQVA si kunngjering i 2024." />
        <DimLine className="mt-10" />
        <ol className="grid gap-8 pt-8 sm:grid-cols-2 lg:grid-cols-5">
          {TIMELINE.map((t) => (
            <li key={t.year}>
              <span className="t-num text-2xl text-ink">{t.year}</span>
              <h3 className="mt-3 text-[17px] font-semibold text-ink">{t.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{t.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="container-public pt-24 sm:pt-32" data-testid="public-about-people">
        <SectionHead eyebrow="Kontaktpersonar" title="Snakk direkte med den som kan svare." text="Du treng ikkje gå via sentralbordet. Ring eller send e-post direkte." />
        <DimLine className="mt-10" />
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {PEOPLE.map((p) => (
            <li key={p.name} className="border-b border-border py-6 sm:pr-8" data-testid="public-person-card">
              <p className="text-[17px] font-semibold text-ink">{p.name}</p>
              <p className="mt-1 text-[14px] leading-snug text-muted-foreground">{p.title}</p>
              <div className="mt-4 flex flex-col gap-1 text-[15px]">
                <a href={telHref(p.phone)} className="u-link tabular w-fit">{p.phone}</a>
                <a href={`mailto:${p.email}`} className="u-link w-fit">{p.email}</a>
              </div>
            </li>
          ))}
        </ul>
        <p className="caption mt-5">Kontaktpersonar slik dei er publiserte på kvinnheradelektro.no.</p>
      </section>

      <section className="container-public pt-24 sm:pt-32" data-testid="public-about-eqva">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
          <Reveal className="order-2 lg:order-1 lg:col-span-5">
            <Photo src={sized(IMG.fish_farm_shore, 1200)} alt="Oppdrettsanlegg i fjorden" ratio="aspect-[4/3] lg:aspect-[4/5]" />
          </Reveal>
          <Reveal delay={0.06} className="order-1 flex flex-col justify-center lg:order-2 lg:col-span-7">
            <Eyebrow className="mb-5">Konsern</Eyebrow>
            <h2 className="t-h2 text-ink">Del av EQVA</h2>
            <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-muted-foreground">EQVA ASA kjøpte Kvinnherad Elektro i 2024. Namnet, leiinga og folka er dei same. Saman med dotterselskapet Vassnes Solutions AS i Ølen og resten av konsernet kan vi ta store, fleirfaglege oppdrag innan elektro, automasjon og mekanisk.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Btn to="/prosjekt" variant="dark" size="md" arrow data-testid="public-about-projects-button">Sjå prosjekt</Btn>
              <Btn to="/karriere" variant="outline" size="md" data-testid="public-about-careers-button">Jobb hos oss</Btn>
            </div>
          </Reveal>
        </div>
      </section>

      <div className="pt-24 sm:pt-32">
        <CTA />
      </div>
    </div>
  );
}
